import { firebaseAuth } from "@/shared/integrations/firebase/client";

/** Demo user email - used for conditional features (e.g. Contact Sales button) */
export const DEMO_USER_EMAIL = "demo@roboticimaging.com";

/** Additional Firebase UIDs treated as "demo" for feature gating.
 *  Some demo accounts sign in with non-demo-shaped emails (e.g. client-branded
 *  addresses), so we identify them by UID instead. Keep this list in sync with
 *  any storage rules that deny these UIDs. */
export const DEMO_USER_UIDS: readonly string[] = [
  "AiCX64fm2zfSz5FC27Znrrm18oD2", // 7-Eleven demo account
];

/**
 * Non-hook demo check for plain async services (e.g. projectsApi) that cannot
 * call the `useIsDemoUser` React hook. Reads `firebaseAuth.currentUser` directly.
 * True when the signed-in user is the canonical demo email OR a UID in the
 * demo allowlist. Keep this and `useIsDemoUser` in agreement.
 */
export const isDemoUser = (): boolean => {
  const user = firebaseAuth?.currentUser;
  if (!user) return false;
  if (user.email === DEMO_USER_EMAIL) return true;
  if (user.uid && DEMO_USER_UIDS.includes(user.uid)) return true;
  return false;
};

/**
 * Read-only backstop for the demo account. Throws if the current user is a demo
 * user, so a mutation fails loudly instead of writing. UI controls are hidden
 * for demo users too (primary gate); this guards the few functional mutation
 * paths a demo could still reach. `action` is named in the error for the toast.
 */
export function assertNotDemo(action = "This action"): void {
  if (isDemoUser()) {
    throw new Error(`${action} is not available in the demo.`);
  }
}

/**
 * Gets the user ID to use for database queries.
 * Returns the Firebase user UID if authenticated, null otherwise.
 * The database now supports TEXT user IDs, so we can use Firebase UIDs directly.
 */
export const getUserIdForDatabase = (): string | null => {
  if (firebaseAuth?.currentUser) {
    return firebaseAuth.currentUser.uid;
  }
  return null;
};

/**
 * Gets the user ID to use for database queries, throwing an error if not authenticated.
 * Use this when you're certain the user should be authenticated.
 * 
 * Returns the Firebase user UID for database operations.
 * The database columns have been migrated to TEXT to support Firebase UIDs.
 */
export const requireUserIdForDatabase = (): string => {
  // Check Firebase auth to ensure user is logged in
  if (!firebaseAuth?.currentUser) {
    throw new Error("User must be authenticated to perform this operation");
  }
  
  // Return the actual Firebase user ID
  // The database now supports TEXT user IDs, so Firebase UIDs work directly
  return firebaseAuth.currentUser.uid;
};
